import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import AddIcon from '@mui/icons-material/Add';
import ListButtons from './ListButtons';
import NavTooltip from './NavTooltip';
import FolderShareModal from '@/features/betaOrganizations/components/FolderShareModal';
import { RouteMap } from '@/routes/Routes';
import { useOrganizationFolders } from '@/features/betaOrganizations/hooks/useOrganizationFolders';
import { BETA_ORGANIZATIONS_FEATURE_ENABLED } from '@/features/betaOrganizations/config';
import type { Organization } from '@/features/betaOrganizations/types';

interface TeamsNavSectionProps {
  isOpen: boolean;
  organizations: Organization[];
}

/**
 * Drilled-in "Team Notes" panel. The Drawer renders this in place of the main
 * nav list whenever the route is a team route. The URL is the single source of
 * truth — every row navigates, so "Back" genuinely changes the URL (and a
 * refresh stays consistent). Rows reuse the same <ListButtons> as the main menu
 * so the styling matches exactly.
 *
 * Single team → its folders directly (Back → My Notes). Multiple teams → the
 * team list at /organizations (Back → My Notes); picking a team opens its
 * folders (Back → the team list).
 */
const TeamsNavSection: React.FC<TeamsNavSectionProps> = ({ isOpen, organizations }) => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const orgPrefix = `${RouteMap.ORGANIZATIONS}/`;
  // Team id comes from the path on a team route, or from the ?org= param when the
  // user has drilled into a team note (/note/:id?org=<teamId>) — so the panel
  // keeps showing that team's folders instead of collapsing.
  const orgParam = new URLSearchParams(search).get('org');
  const teamId = pathname.startsWith(orgPrefix) ? pathname.slice(orgPrefix.length).split('/')[0] : orgParam;
  const isSingleTeam = organizations.length === 1;

  // Only the drilled-into team loads its folders.
  const { folders, refetch } = useOrganizationFolders(
    teamId ?? undefined,
    BETA_ORGANIZATIONS_FEATURE_ENABLED && !!teamId,
  );

  const [createFolderOpen, setCreateFolderOpen] = React.useState(false);

  if (organizations.length === 0) return null;

  // Inside a specific team: Back + General + each folder + Add folder.
  if (teamId) {
    const teamPath = `${RouteMap.ORGANIZATIONS}/${teamId}`;
    const onTeamRoute = pathname === teamPath;
    const activeFolderId = onTeamRoute ? new URLSearchParams(search).get('folder') : null;
    const team = organizations.find((org) => org.id === teamId);

    const buttons = [
      {
        icon: <ArrowBackIcon />,
        text: 'Back',
        onClick: () => navigate(isSingleTeam ? RouteMap.MY_NOTES : RouteMap.ORGANIZATIONS),
        isActive: false,
      },
      {
        icon: <InboxOutlinedIcon />,
        text: 'General',
        onClick: () => navigate(teamPath),
        isActive: onTeamRoute && !activeFolderId,
      },
      ...folders.map((folder) => ({
        icon: <FolderOutlinedIcon />,
        text: folder.name,
        onClick: () => navigate(`${teamPath}?folder=${folder.id}`),
        isActive: onTeamRoute && activeFolderId === folder.id,
      })),
    ];

    return (
      <>
        <ListButtons buttons={buttons} isOpen={isOpen} dense />
        {/* Add folder is an action, not a folder — render it as a gray outlined
            button so it reads as distinct from the folder rows above. */}
        <Box sx={{ px: isOpen ? 2 : 1, pt: 1 }}>
          <NavTooltip label="Add folder" isOpen={isOpen}>
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              fullWidth
              startIcon={isOpen ? <AddIcon /> : undefined}
              onClick={() => setCreateFolderOpen(true)}
              sx={{
                justifyContent: isOpen ? 'flex-start' : 'center',
                minWidth: 0,
                color: 'grey.700',
                borderColor: 'grey.400',
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': { borderColor: 'grey.600', backgroundColor: 'grey.50' },
              }}
            >
              {isOpen ? 'Add folder' : <AddIcon fontSize="small" />}
            </Button>
          </NavTooltip>
        </Box>
        {team && (
          <FolderShareModal
            open={createFolderOpen}
            onClose={() => setCreateFolderOpen(false)}
            organizationId={teamId}
            memberEmails={team.member_emails}
            ownerEmail={team.owner_email}
            onSaved={async (folder) => {
              // Refresh the sidebar list, then drop straight into the new folder.
              await refetch();
              if (folder) navigate(`${teamPath}?folder=${folder.id}`);
            }}
          />
        )}
      </>
    );
  }

  // Team list (multiple teams): Back + a row per team.
  const buttons = [
    {
      icon: <ArrowBackIcon />,
      text: 'Back',
      onClick: () => navigate(RouteMap.MY_NOTES),
      isActive: false,
    },
    ...organizations.map((org) => ({
      icon: <GroupsOutlinedIcon />,
      text: org.name,
      onClick: () => navigate(`${RouteMap.ORGANIZATIONS}/${org.id}`),
      isActive: pathname === `${RouteMap.ORGANIZATIONS}/${org.id}`,
    })),
  ];

  return <ListButtons buttons={buttons} isOpen={isOpen} dense />;
};

export default TeamsNavSection;
